import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { siteConfig } from "../../lib/siteConfig";
import { openInstallPrompt } from "../../components/InstallPrompt";
import { isNativeAdmin } from "../../lib/nativeAdmin";
import {
  BarChart3,
  Bot,
  ClipboardList,
  CreditCard,
  FileCheck2,
  Gamepad2,
  Gift,
  LayoutGrid,
  LogOut,
  Megaphone,
  MessageCircle,
  ReceiptText,
  Send,
  Settings,
  Share2,
  Trophy,
  UserPlus,
  Users,
  WalletCards,
  Menu,
  X,
  Download,
} from "lucide-react";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", shortLabel: "Dashboard", icon: LayoutGrid, permission: "dashboard" },
  { to: "/admin/members", label: "Users", shortLabel: "Users", icon: Users, adminOnly: true },
  { to: "/admin/users", label: "Members", shortLabel: "Members", icon: Users, permission: "members" },
  { to: "/admin/games", label: "Games", shortLabel: "Games", icon: Gamepad2, permission: "games" },
  { to: "/admin/automation", label: "Backend Attachments", shortLabel: "Backend", icon: Bot },
  { to: "/admin/game-credits", label: "Game Credits", shortLabel: "Game Credits", icon: WalletCards, permission: "game_credits" },
  { to: "/admin/requests", label: "Player Requests", shortLabel: "Requests", icon: ClipboardList, permission: "player_requests" },
  { to: "/admin/payment-methods", label: "Payment Methods", shortLabel: "Methods", icon: CreditCard, permission: "payment_methods" },
  { to: "/admin/payment-flows", label: "Payment Flows", shortLabel: "Flows", icon: ReceiptText, adminOnly: true },
  { to: "/admin/wallet", label: "Wallet", shortLabel: "Wallet", icon: WalletCards, permission: "wallet" },
  { to: "/admin/free-credits", label: "Free Credits", shortLabel: "Free Credits", icon: Gift, permission: "free_credits" },
  { to: "/admin/transactions", label: "Transactions", shortLabel: "Transactions", icon: ReceiptText, permission: "transactions" },
  { to: "/admin/payments-review", label: "Payments Review", shortLabel: "Payments", icon: FileCheck2, permission: "payments_review" },
  { to: "/admin/payouts-review", label: "Payouts Review", shortLabel: "Payouts", icon: WalletCards, permission: "payouts_review" },
  { to: "/admin/analytics", label: "Analytics", shortLabel: "Analytics", icon: BarChart3 },
  { to: "/admin/depositors", label: "Depositors", shortLabel: "Depositors", icon: UserPlus, permission: "depositors" },
  { to: "/admin/telegram", label: "Telegram", shortLabel: "Telegram", icon: Send },
  { to: "/admin/communications", label: "Communications", shortLabel: "Notice", icon: Megaphone, permission: "communications" },
  { to: "/admin/support-app", label: "Support App", shortLabel: "Support", icon: MessageCircle, permission: "support_app" },
  { to: "/admin/client-referrals", label: "Client Referrals", shortLabel: "Referrals", icon: Share2, permission: "client_referrals" },
  { to: "/admin/rewards", label: "Rewards", shortLabel: "Rewards", icon: Trophy },
  { to: "/admin/settings", label: "Settings", shortLabel: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    document.body.classList.add("admin-layout-open");
    return () => document.body.classList.remove("admin-layout-open");
  }, []);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  const supportStandaloneMode = location.pathname === "/admin/support-app";
  const supportAppMode = supportStandaloneMode || location.pathname === "/admin/chat" || location.pathname === "/admin/support";

  useEffect(() => {
    if (loading || !user || !["admin", "staff"].includes(user.role)) return undefined;

    let cancelled = false;
    const forceLogout = () => {
      if (cancelled) return;
      logout();
      nav("/admin/login", { replace: true });
    };
    const checkSession = async () => {
      try {
        await api.get("/auth/me", { skipMaintenance: true });
      } catch (error) {
        if (error?.response?.status === 401) forceLogout();
      }
    };
    const handleVisible = () => {
      if (document.visibilityState === "visible") checkSession();
    };

    const timer = window.setInterval(checkSession, 30000);
    window.addEventListener("focus", checkSession);
    document.addEventListener("visibilitychange", handleVisible);
    checkSession();

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", checkSession);
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [loading, logout, nav, user]);

  const loadSupportStats = useCallback(async () => {
    if (loading || !user || !["admin", "staff"].includes(user.role)) return;
    const canUseSupport = user.role === "admin" || (user.admin_permissions || []).includes("support_app");
    if (!canUseSupport) {
      setSupportUnreadCount(0);
      return;
    }
    try {
      const { data } = await api.get("/admin/support/stats", { skipMaintenance: true });
      setSupportUnreadCount(Number(data?.unread_count || 0));
    } catch {
      setSupportUnreadCount(0);
    }
  }, [loading, user]);

  useEffect(() => {
    loadSupportStats();
    const timer = window.setInterval(loadSupportStats, 15000);
    window.addEventListener("focus", loadSupportStats);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", loadSupportStats);
    };
  }, [loadSupportStats]);

  if (loading) return <div className="min-h-screen bg-[#0A0A0B]" />;
  if (!user || !["admin", "staff"].includes(user.role)) return <Navigate to="/admin/login" replace />;

  const isOwner = user.role === "admin";
  const permissions = new Set(user.admin_permissions || []);
  const visibleNav = NAV.filter((item) => isOwner || (!item.adminOnly && item.permission && permissions.has(item.permission)));
  const canUseCurrentRoute = isOwner || visibleNav.some((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));
  if (!canUseCurrentRoute) return <Navigate to={visibleNav[0]?.to || "/admin"} replace />;
  const currentNav = visibleNav.find((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`));

  return (
    <div className={`admin-layout-root safe-area-admin-layout ${supportAppMode ? "admin-layout-support-mode" : ""} ${isNativeAdmin() ? "admin-native-layout admin-force-mobile-nav" : ""} bg-[#0A0A0B] text-slate-200 flex flex-col lg:flex-row`}>
      {!supportStandaloneMode && <header className="admin-mobile-header flex h-14 shrink-0 items-center justify-between border-b border-white/10 bg-[#0A0A0B] px-3 lg:hidden">
        <button type="button" aria-label="Open admin menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)} className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/10 text-white"><Menu className="h-5 w-5" /></button>
        <div className="min-w-0 px-3 text-center"><p className="truncate text-sm font-bold text-white">{currentNav?.shortLabel || currentNav?.label || "Admin"}</p><p className="truncate text-[11px] text-slate-500">{siteConfig.name}</p></div>
        <button type="button" aria-label="Sign out" onClick={() => { logout(); nav("/admin/login"); }} className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/10 text-slate-400"><LogOut className="h-4 w-4" /></button>
      </header>}
      {menuOpen && <button type="button" aria-label="Close admin menu" onClick={() => setMenuOpen(false)} className="admin-menu-overlay fixed inset-0 z-40 bg-black/70 lg:hidden" />}
      <aside data-mobile-open={menuOpen ? "true" : "false"} className={`admin-layout-sidebar ${menuOpen ? "flex" : "hidden lg:flex"} fixed inset-y-0 left-0 z-50 w-[min(82vw,20rem)] shrink-0 border-r border-white/10 bg-[#0A0A0B] flex-col lg:static lg:z-auto lg:w-60`}>
        <div className="px-3 py-4 border-b border-white/10 flex items-center justify-start gap-2 lg:px-5 lg:py-5">
          <img src={siteConfig.logoUrl} alt={siteConfig.name} className="w-8 h-8 rounded-full object-cover ring-1 ring-[#D4AF37]/50" />
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight">{siteConfig.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Operator</div>
          </div>
          <button type="button" aria-label="Close navigation" onClick={() => setMenuOpen(false)} className="admin-drawer-close ml-auto p-2 lg:hidden"><X className="h-5 w-5" /></button>
        </div>
        <nav className="flex-1 p-2 overflow-y-auto">
          {visibleNav.map(({ to, label, shortLabel, icon: Icon }) => {
            const badge = to === "/admin/support-app" ? supportUnreadCount : 0;
            return (
            <NavLink key={to} to={to} data-testid={`admin-nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex min-w-0 items-center justify-start gap-2 rounded-md px-2 py-2 text-xs mb-1 transition-colors sm:text-sm lg:gap-3 lg:px-3 ${
                  isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }>
              <Icon className="w-4 h-4 shrink-0" />
              <span className="min-w-0 truncate lg:hidden">{shortLabel || label}</span>
              <span className="hidden min-w-0 truncate lg:inline">{label}</span>
              {badge > 0 && (
                <span className="ml-auto grid min-w-[1.25rem] shrink-0 place-items-center rounded-full bg-[#D4AF37] px-1.5 py-0.5 text-[10px] font-black leading-none text-black">
                  {badge > 99 ? "99+" : badge}
                </span>
              )}
            </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10">
          {!isNativeAdmin() && <button type="button" onClick={openInstallPrompt} className="mb-2 flex w-full items-center gap-2 rounded-md px-2 py-2 text-xs text-[#D4AF37] lg:px-3"><Download className="h-4 w-4" />Install Admin App</button>}
          <div className="px-2 py-2 mb-2 text-[11px] lg:px-3 lg:text-xs">
            <div className="text-slate-300 truncate">{user.email}</div>
            <div className="text-slate-500">{isOwner ? "Operator" : "Staff"}</div>
          </div>
          <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout"
            className="w-full flex min-w-0 items-center justify-start gap-2 px-2 py-2 rounded-md text-xs text-slate-400 hover:bg-white/5 hover:text-white sm:text-sm lg:px-3">
            <LogOut className="w-4 h-4 shrink-0" />
            <span className="min-w-0 truncate">Sign out</span>
          </button>
        </div>
      </aside>
      <main className={`admin-layout-main flex-1 min-w-0 ${supportAppMode ? "overflow-hidden" : "overflow-auto"}`}>
        <Outlet />
      </main>
    </div>
  );
}
